<template>
    <div v-if="!state.isShowSetting">
        <div class="cloak" @click="$emit('close')"></div>
        <div class="hms-device-selector">
            <ul>
                <li @click="state.isShowSetting = true">
                    Settings
                    <span
                        v-if="!state.isLoading && state.isFree"
                        class="text-red-400 ml-1 font-semibold"
                    >
                        <template
                            v-if="state.livestreamStarted || props.stream"
                        >
                            ({{ formattedTime }})
                        </template>
                        <template v-else> (60m limit) </template>
                    </span>
                </li>
                <li
                    @click="showLiveStream()"
                    :class="{
                        disabled:
                            props.stream == null ||
                            (state.isFree &&
                                state.remainingSeconds <= 0 &&
                                (state.livestreamStarted || props.stream)),
                    }"
                >
                    View Live Stream
                </li>
            </ul>
        </div>
    </div>
    <ModalCustom
        :is-open="state.isShowSetting"
        title="Live Stream Settings"
        @close="closeSettingModal()"
        z-index="1001"
    >
        <div
            class="flex flex-col pb-4 justify-center items-center"
            v-if="state.isLoading"
        >
            <LoadingCircle></LoadingCircle>
        </div>
        <div class="flex flex-col pb-4" v-else>
            <div
                v-if="state.isFree"
                class="
                    mb-4
                    p-3
                    bg-yellow-50
                    border border-yellow-300
                    text-yellow-800
                    rounded
                "
            >
                <div class="font-bold flex items-center gap-2">
                    <ion-icon name="time-outline"></ion-icon>
                    <span>Free Team Livestream Limit</span>
                </div>
                <p class="text-xs mt-1 text-yellow-700">
                    Livestreams for free teams are limited to 15 minutes.
                </p>
                <div class="text-xl font-mono mt-2 font-semibold">
                    <span v-if="state.livestreamStarted || props.stream">
                        Remaining Time:
                        <span
                            :class="
                                state.remainingSeconds > 300
                                    ? 'text-green-600'
                                    : 'text-red-600'
                            "
                            >{{ formattedTime }}</span
                        >
                    </span>
                    <span v-else>
                        Remaining Time:
                        <span class="text-gray-600">15:00 (Not Started)</span>
                    </span>
                </div>
            </div>

            <template
                v-if="
                    !state.isFree ||
                    state.remainingSeconds > 0 ||
                    !(state.livestreamStarted || props.stream)
                "
            >
                <h3 class="text-lg font-semibold">RTMP URL</h3>
                <code>{{ state.url }}</code>
                <h3 class="text-lg font-semibold mt-2">RTMP Key</h3>
                <code>{{ state.key }}</code>
            </template>
            <div
                v-else
                class="
                    p-3
                    bg-red-100
                    border border-red-400
                    text-red-700
                    rounded
                    text-center
                    mt-2
                "
            >
                <div class="font-bold">15-Minute Limit Reached</div>
                <p class="text-sm mt-1 text-red-600">
                    The 15-minute limit for free teams has been reached. Please
                    upgrade to a paid team subscription to continue
                    livestreaming.
                </p>
            </div>
        </div>
    </ModalCustom>
</template>

<script setup>
import {
    reactive,
    defineProps,
    defineEmits,
    onMounted,
    computed,
    onUnmounted,
    watch,
} from "vue";
import emitter from "tiny-emitter/instance";
const emit = defineEmits(["close"]);

const props = defineProps(["stream"]);

const state = reactive({
    isShowSetting: false,
    isLoading: true,
    url: "",
    key: "",
    isFree: false,
    remainingSeconds: 15 * 60, // 15 minutes default for free
    livestreamStarted: false,
});

let countdownInterval = null;

const formattedTime = computed(() => {
    if (state.remainingSeconds <= 0) {
        return "00:00";
    }
    const hours = Math.floor(state.remainingSeconds / 3600);
    const minutes = Math.floor((state.remainingSeconds % 3600) / 60);
    const seconds = state.remainingSeconds % 60;

    const parts = [];
    if (hours > 0) {
        parts.push(String(hours).padStart(2, "0"));
    }
    parts.push(String(minutes).padStart(2, "0"));
    parts.push(String(seconds).padStart(2, "0"));

    return parts.join(":");
});

function startCountdown() {
    if (countdownInterval) {
        clearInterval(countdownInterval);
    }
    countdownInterval = setInterval(() => {
        if (state.remainingSeconds > 0) {
            state.remainingSeconds--;
        } else {
            clearInterval(countdownInterval);
        }
    }, 1000);
}

function closeSettingModal() {
    state.isShowSetting = false;
    emit("close");
}

async function getStreamKey() {
    try {
        state.isLoading = true;
        const roomCode = new URLSearchParams(window.location.search).get(
            "code"
        );

        const data = await axios.get("/meeting/stream-key", {
            params: {
                code: roomCode,
            },
        });
        state.url = data.data.url;
        state.key = data.data.key;
        state.isFree = data.data.is_free;
        state.remainingSeconds =
            data.data.remaining_seconds !== null
                ? data.data.remaining_seconds
                : 3600;
        state.livestreamStarted = data.data.livestream_started || false;

        if (
            state.isFree &&
            state.remainingSeconds > 0 &&
            (state.livestreamStarted || props.stream)
        ) {
            startCountdown();
        }
    } catch (error) {}
    state.isLoading = false;
}

function showLiveStream() {
    if (props.stream == null) return;
    if (state.isFree && state.remainingSeconds <= 0) return;

    emitter.emit("livestream-loaded", {
        mode: "stacked-video",
        video: {
            title: "Live Stream",
            url: props.stream,
            isLiveStream: true,
        },
    });
    emit("close");
}

watch(
    () => props.stream,
    (newVal) => {
        if (newVal) {
            state.livestreamStarted = true;
            getStreamKey();
        } else {
            state.livestreamStarted = false;
            if (countdownInterval) {
                clearInterval(countdownInterval);
            }
        }
    }
);

onMounted(() => {
    getStreamKey();
});

onUnmounted(() => {
    if (countdownInterval) {
        clearInterval(countdownInterval);
    }
});
</script>

<style scoped>
.cloak {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    z-index: 2000;
    background: #0001;
}

.hms-device-selector {
    position: absolute;
    bottom: 50px;
    left: 0;
    z-index: 2001;
    background: #333;
    padding: 16px;
    border-radius: 4px;
    color: #fff;
    width: 250px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.25), 0 2px 4px rgba(0, 0, 0, 0.1);
}

h3 {
    font-size: 16px;
    margin-top: 8px;
}

ul {
    list-style: none;
    margin: 0;
    padding: 0;
    font-size: 12px;
}

ul li {
    padding: 8px;
    border-radius: 4px;
}

ul li:hover {
    background: #555;
    cursor: pointer;
}

ul li.active {
    background: #38f;
}

ul li.disabled {
    color: #555;
}
code {
    background: #d7d7d7;
    padding: 8px;
    border-radius: 4px;
}
</style>
